import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Phone, Download, Star, Trash2, CheckCircle, AlertCircle,
  Sparkle, User, Calendar, Clock, Loader2, Award
} from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, LineChart, Line
} from 'recharts';
import { useTheme } from '../zustand/ThemeStore';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserStore } from '../zustand/UserStore';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';
import CallFilters from '../components/filters/CallFilters';

const ITEMS_PER_PAGE = 6;

export default function UserDashboard() {
  const isDarkMode = useTheme((set)=>set.theme)
  const [activeTab, setActiveTab] = useState('analysis');
  const [dateFilter, setDateFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [scoreFilter, setScoreFilter] = useState<'all' | 'top5' | 'top10'>('all');
  const [customDateRange, setCustomDateRange] = useState<{
    startDate: string;
    endDate: string;
  } | undefined>(undefined);
  const [customDurationRange, setCustomDurationRange] = useState<{
    minDuration: number;
    maxDuration: number;
  } | undefined>(undefined);
  const [userCalls, setUserCalls] = useState<any[]>([]);
  const [totalCallsCount, setTotalCallsCount] = useState<number>(0);
  const [targetUserData, setTargetUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [analyzingPerformance, setAnalyzingPerformance] = useState(false);
  const [deletingCall, setDeletingCall] = useState<string | null>(null);
  const [downloadingCall, setDownloadingCall] = useState<string | null>(null);
  const [dailyScores, setDailyScores] = useState<any[]>([]);
  const [loadingDailyScores, setLoadingDailyScores] = useState(false);
  const [selectedTrendFilter, setSelectedTrendFilter] = useState<string>('last30');

  // Build month options for the trend filter (last 12 months)
  const trendMonthOptions = (() => {
    const options: { value: string; label: string }[] = [
      { value: 'last30', label: 'Last 30 Days' }
    ];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${d.getMonth() + 1}`;
      const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  })();
  const [rankingData, setRankingData] = useState<{
    current_rank: number | null;
    total_ranked_users: number | null;
    rank_updated_at: string | null;
  } | null>(null);
  const [loadingRank, setLoadingRank] = useState(false);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });
  
  // Lazy loading state for recordings
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreCalls, setHasMoreCalls] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  
  const nav = useNavigate();
  const params = useParams();
  const user = useUserStore((state) => state.user);
  
  // Get user ID from URL params or current user
  const targetUserId = params.userId || user?.uid;
  const isOwnProfile = !params.userId || params.userId === user?.uid;

  // Keep these variables to prevent unused warnings
  console.log('Debug info:', { activeTab, dateFilter, searchQuery, userCalls, loading, nav });

  // Modal helper functions
  const showModal = (type: 'success' | 'error' | 'confirm', title: string, message: string, onConfirm?: () => void) => {
    setModalState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user's calls with pagination
  const fetchCallsPage = useCallback(async (page: number, append: boolean = false) => {
    if (!targetUserId) return;
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const token = localStorage.getItem('token');
      
      console.log(`Fetching calls page ${page}, append: ${append}`);
      
      // Fetch paginated calls
      const callsResponse = await axios.get(
        `${getApiBaseUrl()}/api/v1/calls/user/${targetUserId}?metadata_only=false&page=${page}&limit=${ITEMS_PER_PAGE}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newCalls = callsResponse.data.calls || [];
      const pagination = callsResponse.data.pagination;

      // Capture total count from API pagination (same as CallRecordingsGrid)
      const apiTotalCount = pagination?.total_count || 0;
      console.log('Total count from API pagination:', apiTotalCount);

      if (append) {
        setUserCalls(prev => [...prev, ...newCalls]);
      } else {
        setUserCalls(newCalls);
        // Only set total on the first page fetch so header shows real total
        setTotalCallsCount(apiTotalCount);
      }

      setCurrentPage(pagination.current_page);
      setHasMoreCalls(pagination.has_next);
      
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [targetUserId]);

  // Fetch user's calls and performance data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!targetUserId) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // If viewing another user's profile, fetch their user data
        if (!isOwnProfile) {
          try {
            const userResponse = await axios.get(`${getApiBaseUrl()}/api/v1/users/${targetUserId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setTargetUserData(userResponse.data);
          } catch (userError) {
            console.log('Could not fetch target user data:', userError);
            setTargetUserData(null);
          }
        }
        
        // Fetch first page of calls
        await fetchCallsPage(1, false);
        
        // Fetch user's performance metadata
        try {
          const metadataResponse = await axios.get(`${getApiBaseUrl()}/api/v1/user/${targetUserId}/metadata`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPerformanceData(metadataResponse.data.data);
        } catch (metadataError) {
          console.log('Could not fetch performance metadata:', metadataError);
          setPerformanceData(null);
        }

        // Fetch user's ranking data
        try {
          setLoadingRank(true);
          const rankResponse = await axios.get(`${getApiBaseUrl()}/api/v1/user/${targetUserId}/rank`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (rankResponse.data.success && rankResponse.data.data) {
            setRankingData(rankResponse.data.data);
          } else {
            setRankingData(null);
          }
        } catch (rankError) {
          console.log('Could not fetch ranking data:', rankError);
          setRankingData(null);
        } finally {
          setLoadingRank(false);
        }
        
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [targetUserId, isOwnProfile, fetchCallsPage]);

  // Fetch day-wise scores (separate effect so it re-runs on filter change)
  useEffect(() => {
    if (!targetUserId) return;
    const fetchDailyScores = async () => {
      try {
        setLoadingDailyScores(true);
        const token = localStorage.getItem('token');
        let dailyScoresUrl = `${getApiBaseUrl()}/api/v1/user/${targetUserId}/daily-scores`;
        if (selectedTrendFilter === 'last30') {
          dailyScoresUrl += '?days=30';
        } else {
          const [yr, mo] = selectedTrendFilter.split('-');
          dailyScoresUrl += `?month=${mo}&year=${yr}`;
        }
        const response = await axios.get(dailyScoresUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDailyScores(response.data.daily_scores || []);
      } catch (error) {
        console.log('Could not fetch daily scores:', error);
        setDailyScores([]);
      } finally {
        setLoadingDailyScores(false);
      }
    };
    fetchDailyScores();
  }, [targetUserId, selectedTrendFilter]);

  // Load more calls callback
  const loadMoreCalls = useCallback(() => {
    if (hasMoreCalls && !loadingMore && !loading) {
      fetchCallsPage(currentPage + 1, true);
    }
  }, [hasMoreCalls, loadingMore, loading, currentPage, fetchCallsPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Only set up observer when on recordings tab
    if (activeTab !== 'recordings') return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreCalls && !loadingMore && !loading) {
          console.log('Loading more calls...', { currentPage, hasMoreCalls, loadingMore, loading });
          loadMoreCalls();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
      console.log('Observer attached to target');
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loadMoreCalls, hasMoreCalls, loadingMore, loading, activeTab, currentPage]);

  // Function to trigger performance analysis
  const analyzeUserPerformance = async () => {
    if (!targetUserId) return;
    
    try {
      setAnalyzingPerformance(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${getApiBaseUrl()}/api/v1/user/${targetUserId}/analyze-performance`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Refresh performance data after analysis
        const metadataResponse = await axios.get(`${getApiBaseUrl()}/api/v1/user/${targetUserId}/metadata`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPerformanceData(metadataResponse.data.data);
        
        // Refresh ranking data after analysis
        try {
          const rankResponse = await axios.get(`${getApiBaseUrl()}/api/v1/user/${targetUserId}/rank`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (rankResponse.data.success && rankResponse.data.data) {
            setRankingData(rankResponse.data.data);
          }
        } catch (rankError) {
          console.log('Could not refresh ranking data:', rankError);
        }
        
        // Show success modal
        showModal('success', 'Analysis Complete', 'Performance analysis completed successfully!');
      }
    } catch (error) {
      console.error('Error analyzing performance:', error);
      showModal('error', 'Analysis Failed', 'Failed to analyze performance. Please try again.');
    } finally {
      setAnalyzingPerformance(false);
    }
  };

  // Use real user data from store or fetched data with hardcoded performance metrics
  const userData: any = {
    name: isOwnProfile 
      ? (user?.name || "User") 
      : (targetUserData?.name || "User"),
    role: isOwnProfile 
      ? (user?.designation || "Sales Representative") 
      : (targetUserData?.designation || "Sales Representative"),
    team: isOwnProfile 
      ? "Sales Team" 
      : (targetUserData?.role || "Sales Team"),
    joinDate: isOwnProfile
      ? (user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : "Unknown")
      : (targetUserData?.created_at ? new Date(targetUserData.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : "Unknown"),
    avatar: (() => {
      const name = isOwnProfile 
        ? user?.name 
        : (targetUserData?.name);
      return name ? name.split(' ').map((n: string) => n[0]).join('').toUpperCase() : "U";
    })(),
    stats: {
      totalCalls: totalCallsCount || 0,
      avgScore: performanceData?.has_performance_data && performanceData?.current_performance?.overall_performance?.average_score 
        ? performanceData.current_performance.overall_performance.average_score 
        : (userCalls.length > 0 ? 0 : 0),
      rank: rankingData?.current_rank || null,
      totalRankedUsers: rankingData?.total_ranked_users || null,
      rankUpdatedAt: rankingData?.rank_updated_at || null
    }
  };

  // Use hardcoded performance data
  const cumulativeData: any = {
    representative: userData.name,
    totalCalls: userData.stats.totalCalls,
    averageRepScore: userData.stats.avgScore,
    repSubscores: (() => {
      // First priority: use core_metrics directly from targetUserData if available
      if (targetUserData?.core_metrics) {
        return {
          rapport_setting_expectations: targetUserData.core_metrics.rapport_setting_expectations || null,
          pain_extraction: targetUserData.core_metrics.pain_extraction || null,
          timeline_discovery: targetUserData.core_metrics.timeline_discovery || null,
          decision_maker_discovery: targetUserData.core_metrics.decision_maker_discovery || null,
          next_step_commitment: targetUserData.core_metrics.next_step_commitment || null
        };
      }
      // Second priority: use performance data core_metrics
      if (performanceData?.has_performance_data && performanceData?.current_performance?.core_metrics) {
        return {
          rapport_setting_expectations: performanceData.current_performance.core_metrics.rapport_setting_expectations?.score || null,
          pain_extraction: performanceData.current_performance.core_metrics.pain_extraction?.score || null,
          timeline_discovery: performanceData.current_performance.core_metrics.timeline_discovery?.score || null,
          decision_maker_discovery: performanceData.current_performance.core_metrics.decision_maker_discovery?.score || null,
          next_step_commitment: performanceData.current_performance.core_metrics.next_step_commitment?.score || null
        };
      }
      // Fallback: return null values when no performance analysis is present
      return {
        rapport_setting_expectations: null,
        pain_extraction: null,
        timeline_discovery: null,
        decision_maker_discovery: null,
        next_step_commitment: null
      };
    })(),
    repScoreTrend: userCalls.length > 0 ? [6.5, 7.2, 7.8, 8.1, 7.8] : [],
    callNumbers: userCalls.length > 0 ? [1, 2, 3, 4, 5] : []
  };

  // Function to handle audio file download
  const handleDownloadAudio = async (recording: any) => {
    try {
      setDownloadingCall(recording.call_id);
      let audioUrl = recording.audio_url;
      
      console.log('Download attempt for recording:', {
        callId: recording.call_id,
        customer: recording.customer_name,
        fileType: recording.file_type,
        hasAudioUrl: !!audioUrl
      });
      
      // If audio URL is not available in the recording data, try to fetch it
      if (!audioUrl) {
        console.log('Audio URL not found in recording data, fetching from API...');
        const token = localStorage.getItem('token');
        
        // Get call details including audio URL
        const response = await axios.get(`${getApiBaseUrl()}/api/v1/calls/${recording.call_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const callData = response.data.call;
        audioUrl = callData.audio_url;
        console.log('Audio URL from API:', audioUrl);
      }
      
      if (!audioUrl) {
        showModal('error', 'Download Failed', 'Audio file not available for download');
        return;
      }
      
      console.log('Starting download from URL:', audioUrl);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `${recording.customer_name}_call_${recording.call_id}.mp3`;
      link.target = '_blank';
      
      // For cross-origin downloads, we might need to fetch and create blob
      try {
        const audioResponse = await fetch(audioUrl);
        const blob = await audioResponse.blob();
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL after download
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        console.log('Download initiated successfully');
        showModal('success', 'Download Started', 'Audio file download has been initiated successfully.');
      } catch (fetchError) {
        console.log('Blob download failed, trying direct download:', fetchError);
        // Fallback: try direct download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showModal('success', 'Download Started', 'Audio file download has been initiated successfully.');
      }
      
    } catch (error) {
      console.error('Error downloading audio file:', error);
      showModal('error', 'Download Failed', 'Failed to download audio file. Please try again.');
    } finally {
      setDownloadingCall(null);
    }
  };

  // Function to handle call deletion
  const handleDeleteCall = async (recording: any) => {
    const performDelete = async () => {
      try {
        setDeletingCall(recording.call_id);
        const token = localStorage.getItem('token');
        
        await axios.delete(`${getApiBaseUrl()}/api/v1/calls/${recording.call_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Remove the call from the local state
        setUserCalls(prevCalls => prevCalls.filter(call => call.id !== recording.call_id));
        
        showModal('success', 'Recording Deleted', 'Recording deleted successfully');
      } catch (error) {
        console.error('Error deleting recording:', error);
        showModal('error', 'Deletion Failed', 'Failed to delete recording. Please try again.');
      } finally {
        setDeletingCall(null);
      }
    };

    // Show confirmation modal
    showModal(
      'confirm',
      'Delete Recording',
      `Are you sure you want to delete the recording for ${recording.customer_name}? This action cannot be undone.`,
      performDelete
    );
  };

  // Use actual user calls data instead of mock data
  const recordings = userCalls.map((call, index) => {
    const callDate = call.timestamp ? new Date(call.timestamp) : null;
    const formattedDate = callDate ? callDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) : 'Unknown Date';
    
    const formattedTime = callDate ? callDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    }) : '';
    
    return {
      id: call.id || index,
      title: call.customer_name ? `${call.customer_name}` : 'Unknown Customer',
      customer_name: call.customer_name || 'Unknown Customer',
      representative: call.representative || userData.name,
      date: formattedDate,
      time: formattedTime,
      timestamp: call.timestamp,
      duration: call.call_duration || call.duration || "0:00",
      score: typeof call.total_score === 'number' ? call.total_score.toFixed(1) : 
             (call.data?.overall_average ? call.data.overall_average.toFixed(1) : '0.0'),
      status: call.status || 'Completed',
      call_id: call.id,
      file_type: call.file_type || 'audio',
      audio_url: call.audio_url // Include audio URL for download
    };
  });

  // Filter recordings
  const filteredRecordings = useMemo(() => {
    let filtered = recordings;

    // Date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      const filterDate = new Date();

      if (dateFilter === 'custom' && customDateRange) {
        const startDate = new Date(customDateRange.startDate);
        const endDate = new Date(customDateRange.endDate);
        filtered = filtered.filter(rec => {
          if (!rec.timestamp) return false;
          const recordDate = new Date(rec.timestamp);
          return recordDate >= startDate && recordDate <= endDate;
        });
      } else {
        switch (dateFilter) {
          case 'today':
            filterDate.setDate(today.getDate());
            break;
          case 'week':
            filterDate.setDate(today.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(today.getMonth() - 1);
            break;
          case 'quarter':
            filterDate.setMonth(today.getMonth() - 3);
            break;
        }

        filtered = filtered.filter(rec => {
          if (!rec.timestamp) return false;
          return new Date(rec.timestamp) >= filterDate;
        });
      }
    }

    // Duration filter
    if (durationFilter !== 'all') {
      filtered = filtered.filter(rec => {
        if (!rec.duration || rec.duration === "N/A" || rec.duration === "0:00") return false;
        
        // Parse duration to minutes
        const [minutes, seconds] = rec.duration.split(':').map(Number);
        const totalMinutes = minutes + (seconds / 60);

        if (durationFilter === 'custom' && customDurationRange) {
          return totalMinutes >= customDurationRange.minDuration && totalMinutes <= customDurationRange.maxDuration;
        }

        switch (durationFilter) {
          case 'short': // Less than 5 minutes
            return totalMinutes < 5;
          case 'medium': // 5-15 minutes
            return totalMinutes >= 5 && totalMinutes <= 15;
          case 'long': // More than 15 minutes
            return totalMinutes > 15;
          default:
            return true;
        }
      });
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(rec =>
        rec.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.representative.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Score filter - Sort by score and take top N
    if (scoreFilter !== 'all') {
      // First, sort all recordings by score in descending order
      const sorted = [...filtered].sort((a, b) => parseFloat(b.score) - parseFloat(a.score));
      
      // Then take top 5 or top 10 based on filter
      const topN = scoreFilter === 'top5' ? 5 : 10;
      filtered = sorted.slice(0, topN);
    }

    return filtered;
  }, [recordings, dateFilter, durationFilter, searchQuery, scoreFilter, customDateRange, customDurationRange]);

  // Colour for each day bar based on score
  const getDayScoreColor = (score: number) => {
    if (score >= 8) return '#1a9f6b';
    if (score >= 6) return '#fbbf24';
    if (score > 0) return '#ef4444';
    return '#374151'; // no-data grey
  };

  const chartPerformanceData: any = Object.entries(cumulativeData.repSubscores).map(([key, value]) => {
    // Check if value exists and is valid
    const hasValue = value != null && value !== undefined && !isNaN(value as number);
    const score = value as number;
    const displayScore = hasValue ? Math.min(10, Math.max(1, score)) : 0;
    return {
      area: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      score: displayScore,
      fullMark: 10
    };
  });

  const getPerformanceColor = (score: number) => {
    if (score >= 8) return '#1a9f6b';
    if (score >= 6) return '#fbbf24';
    return '#ef4444';
  };

  const getScoreBadgeColor = (score: number) => {
    if (score >= 8) return isDarkMode ? 'bg-green-900/20 text-green-400 border-green-700' : 'bg-green-100 text-green-700 border-green-300';
    if (score >= 6) return isDarkMode ? 'bg-yellow-900/20 text-yellow-400 border-yellow-700' : 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return isDarkMode ? 'bg-red-900/20 text-red-400 border-red-700' : 'bg-red-100 text-red-700 border-red-300';
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className={`text-lg ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
            Loading user dashboard...
          </div>
        </div>
      ) : (
        <>
          {/* Header Section */}
          <div className={`border-b ${
            isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
          }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                {/* Avatar */}
                <div className="w-24 h-24 bg-gradient-to-br from-[#1a9f6b] to-[#148856] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {userData.avatar}
                </div>

                {/* User Info */}
                <div className="flex-1 text-center sm:text-left">
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    {userData.name}
                    {!isOwnProfile && (
                      <span className={`ml-2 text-sm font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        (Team Member)
                      </span>
                    )}
                  </h2>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                    {userData.role} • {userData.team}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                    Member since {userData.joinDate}
                  </p>
                </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className={`rounded-lg p-3 text-center border ${
                isDarkMode 
                  ? 'bg-[#050a0a] border-[#1a2424]' 
                  : 'bg-white border-gray-200'
              }`}>
                <Phone className={`w-5 h-5 mx-auto mb-1 ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`} />
                <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                  {userData.stats.totalCalls}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>Total Calls</div>
              </div>
              <div className={`rounded-lg p-3 text-center border ${
                isDarkMode 
                  ? 'bg-[#050a0a] border-[#1a2424]' 
                  : 'bg-white border-gray-200'
              }`}>
                <Star className={`w-5 h-5 mx-auto mb-1 ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`} />
                <div className={`text-lg font-bold`} style={{ color: getPerformanceColor(userData.stats.avgScore) }}>
                  {userData.stats.avgScore}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>Avg Score</div>
              </div>
              <div className={`rounded-lg p-3 text-center border ${
                isDarkMode 
                  ? 'bg-[#050a0a] border-[#1a2424]' 
                  : 'bg-white border-gray-200'
              }`}>
                <Award className={`w-5 h-5 mx-auto mb-1 ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`} />
                {loadingRank ? (
                  <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  </div>
                ) : userData.stats.rank ? (
                  <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    #{userData.stats.rank}
                    {userData.stats.totalRankedUsers && (
                      <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                      </span>
                    )}
                  </div>
                ) : (
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                    Not Ranked
                  </div>
                )}
                <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                  {userData.stats.rank ? 'Team Rank' : 'No Ranking'}
                </div>
              </div>
              <div className={`rounded-lg p-2 text-center border ${
                isDarkMode 
                  ? 'bg-[#050a0a] border-[#1a2424]' 
                  : 'bg-white border-gray-200'
              }`}>
                <button
                  onClick={analyzeUserPerformance}
                  disabled={analyzingPerformance || totalCallsCount === 0}
                  className={`w-full h-full flex flex-col items-center justify-center transition-all ${
                    analyzingPerformance || totalCallsCount === 0
                      ? `cursor-not-allowed ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`
                      : `cursor-pointer hover:scale-105 ${isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'}`
                  }`}
                  title={totalCallsCount === 0 ? "No calls to analyze" : "Analyze today's performance"}
                >
                  <Sparkle className={`w-5 h-5 mb-1 ${analyzingPerformance ? 'animate-spin' : ''}`} />
                  <div className={`text-sm font-bold`}>
                    {analyzingPerformance ? 'Analyzing...' : 'Analyze'}
                  </div>
                  <div className={`text-xs`}>
                    Performance
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`border-b ${
        isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('analysis')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'analysis'
                  ? 'border-[#1a9f6b] text-[#1a9f6b]'
                  : `${isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
              }`}
            >
              Performance Analysis
            </button>
            <button
              onClick={() => setActiveTab('recordings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'recordings'
                  ? 'border-[#1a9f6b] text-[#1a9f6b]'
                  : `${isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
              }`}
            >
              Call Recordings
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'analysis' ? (
          /* Cumulative Analysis Section */
          <div className="space-y-6">
            {/* Performance Overview */}
            <div className={`rounded-xl p-6 shadow-lg ${
              isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
            }`}>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'} mb-6`}>
                Performance Overview
              </h3>

              {/* Skill Scores Grid */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                {Object.entries(cumulativeData.repSubscores).map(([key, value]) => {
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  // Check if value exists and is valid, otherwise show N/A
                  const hasValue = value != null && value !== undefined && !isNaN(value as number);
                  const clampedScore = hasValue ? Math.min(10, Math.max(1, value as number)) : 0;
                  const percentage = hasValue ? (clampedScore / 10) * 100 : 0;
                  const displayValue = hasValue ? clampedScore.toFixed(1) : 'N/A';
                  const displayPercentage = hasValue ? `(${percentage.toFixed(0)}%)` : '';
                  
                  return (
                    <div key={key} className={`rounded-lg p-4 border ${
                      isDarkMode 
                        ? 'bg-[#050a0a] border-[#1a2424]' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className={`text-[0.84rem] ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'} mb-2`}>
                        {label}
                      </div>
                      <div className={`text-2xl font-bold mb-1 ${
                        hasValue 
                          ? '' 
                          : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                      }`} style={{ color: hasValue ? getPerformanceColor(clampedScore) : undefined }}>
                        {displayValue}
                      </div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {hasValue ? `out of 10 ${displayPercentage}` : 'No data available'}
                      </div>
                      <div className={`mt-2 w-full ${isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-300'} rounded-full h-2`}>
                        <div
                          className={`bg-gradient-to-r from-[#1a9f6b] to-[#148856] h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bar Chart - Full Width */}
              <div className="mb-8 py-4">
                <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'} mb-4`}>
                  Skills Performance Overview
                </h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#1a2424' : '#E5E7EB'} />
                      <XAxis
                        dataKey="area"
                        tick={{ fontSize: 10, fill: isDarkMode ? '#9CA3AF' : '#6B7280' }}
                        angle={-45}
                        textAnchor="end"
                        height={120}
                      />
                      <YAxis
                        domain={[0, 10]}
                        tick={{ fontSize: 10, fill: isDarkMode ? '#9CA3AF' : '#6B7280' }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: isDarkMode ? '#0a1414' : '#FFFFFF',
                          border: `1px solid ${isDarkMode ? '#1a2424' : '#E5E7EB'}`,
                          borderRadius: '8px',
                          color: isDarkMode ? 'white' : 'black'
                        }}
                      />
                      <Bar
                        dataKey="score"
                        fill="#1a9f6b"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                {/* Daily Scores Trend Chart */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      Daily Scores
                    </h4>
                    <div className="flex items-center gap-3">
                      {dailyScores.filter(d => d.calls > 0).length > 0 && (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {dailyScores.filter(d => d.calls > 0).length} active day{dailyScores.filter(d => d.calls > 0).length !== 1 ? 's' : ''}
                        </span>
                      )}
                      <select
                        value={selectedTrendFilter}
                        onChange={(e) => setSelectedTrendFilter(e.target.value)}
                        className={`text-sm rounded-lg border px-3 py-1.5 outline-none cursor-pointer ${
                          isDarkMode
                            ? 'bg-[#050a0a] border-[#1a2424] text-white'
                            : 'bg-gray-50 border-gray-200 text-[#053333]'
                        }`}
                      >
                        {trendMonthOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="h-64">
                    {loadingDailyScores ? (
                      <div className={`h-full flex items-center justify-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Loading daily scores...
                      </div>
                    ) : dailyScores.some((d: any) => d.calls > 0) ? (
                      <>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={dailyScores.map((d: any) => ({ ...d, score: d.calls > 0 ? d.score : null }))}
                            margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#1a2424' : '#E5E7EB'} />
                            <XAxis dataKey="label" stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} fontSize={11} interval={2} />
                            <YAxis stroke={isDarkMode ? '#9CA3AF' : '#6B7280'} fontSize={11} domain={[0, 10]} />
                            <Tooltip
                              content={({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0]?.payload;
                                return (
                                  <div className={`p-2 rounded-lg border text-sm ${
                                    isDarkMode ? 'bg-[#0a1414] border-[#1a2424] text-white' : 'bg-white border-gray-200 text-[#053333]'
                                  }`}>
                                    <p className="font-semibold">{d.date}</p>
                                    {d.calls > 0 ? (
                                      <>
                                        <p style={{ color: getDayScoreColor(d.score) }}>Score: {d.score.toFixed(1)}</p>
                                        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{d.calls} call{d.calls !== 1 ? 's' : ''}</p>
                                      </>
                                    ) : (
                                      <p className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>No calls</p>
                                    )}
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="score"
                              stroke="#1a9f6b"
                              strokeWidth={2.5}
                              connectNulls={false}
                              dot={(props: any) => {
                                const { cx, cy, payload } = props;
                                if (payload.calls === 0 || payload.score == null) return <g key={props.key} />;
                                return (
                                  <circle
                                    key={props.key}
                                    cx={cx} cy={cy} r={5}
                                    fill={getDayScoreColor(payload.score)}
                                    stroke={isDarkMode ? '#0a1414' : '#fff'}
                                    strokeWidth={2}
                                  />
                                );
                              }}
                              activeDot={(props: any) => {
                                const { cx, cy, payload } = props;
                                return (
                                  <circle
                                    key={props.key}
                                    cx={cx} cy={cy} r={7}
                                    fill={getDayScoreColor(payload.score)}
                                    stroke={isDarkMode ? '#0a1414' : '#fff'}
                                    strokeWidth={2}
                                  />
                                );
                              }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </>
                    ) : (
                      <div className={`h-full flex items-center justify-center border rounded-lg ${
                        isDarkMode ? 'border-[#1a2424] bg-[#050a0a]' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="text-center space-y-3">
                          <div className="text-4xl">📅</div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            No calls in {selectedTrendFilter === 'last30' ? 'the last 30 days' : trendMonthOptions.find(o => o.value === selectedTrendFilter)?.label || 'this period'}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Upload a call recording to start tracking your performance
                          </p>
                          <button
                            onClick={() => nav('/analysis')}
                            className="mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#1a9f6b] text-white hover:bg-[#148856] transition-colors"
                          >
                            <span>Upload &amp; Analyze</span>
                            <span>→</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Skills Chart */}
                {/* <div>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      Skills Assessment
                    </h4>
                  </div>
                  <div className="h-64">
                    {Object.values(cumulativeData.repSubscores).some((value: any) => value !== null && value !== undefined) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={chartPerformanceData}>
                          <PolarGrid stroke={isDarkMode ? '#1a2424' : '#E5E7EB'} />
                          <PolarAngleAxis
                            dataKey="area"
                            tick={{ fontSize: 10, fill: isDarkMode ? '#9CA3AF' : '#6B7280' }}
                          />
                          <PolarRadiusAxis
                            angle={90}
                            domain={[0, 10]}
                            tick={{ fontSize: 10, fill: isDarkMode ? '#6B7280' : '#9CA3AF' }}
                          />
                          <Radar
                            dataKey="score"
                            stroke="#1a9f6b"
                            fill="#1a9f6b"
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className={`h-full flex items-center justify-center border rounded-lg ${
                        isDarkMode ? 'border-[#1a2424] bg-[#050a0a]' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="text-center">
                          <div className={`text-4xl mb-2`}>🎯</div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Skills assessment will appear<br />after call analysis
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div> */}
              </div>
            </div>

            {/* Performance Analysis Overview */}
            <div className={`rounded-xl p-6 shadow-lg ${
              isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
            }`}>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'} mb-6`}>
                Performance Analysis Overview
              </h3>
              
              <div className="space-y-6">
                {!performanceData?.has_performance_data ? (
                  totalCallsCount === 0 ? (
                    /* New user with no calls */
                    <div className={`rounded-lg p-6 border text-center ${
                      isDarkMode 
                        ? 'bg-gray-900/30 border-gray-700' 
                        : 'bg-gray-50 border-gray-300'
                    }`}>
                      <div className={`text-6xl mb-4`}>📊</div>
                      <h4 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                        Welcome to Your Performance Dashboard!
                      </h4>
                      <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
                        Upload your first sales call recording to start tracking your performance metrics.
                      </p>
                      <div className={`rounded-lg p-4 border ${
                        isDarkMode 
                          ? 'bg-blue-900/20 border-blue-700' 
                          : 'bg-blue-50 border-blue-300'
                      }`}>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                          📊 Your dashboard will show detailed performance analysis once you have call recordings.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* User has calls but no performance analysis yet */
                    <div className={`rounded-lg p-6 border text-center ${
                      isDarkMode 
                        ? 'bg-yellow-900/30 border-yellow-700' 
                        : 'bg-yellow-50 border-yellow-300'
                    }`}>
                      <div className={`text-6xl mb-4`}>🔍</div>
                      <h4 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                        Ready for Performance Analysis!
                      </h4>
                      <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
                        You have {totalCallsCount} call recording{totalCallsCount > 1 ? 's' : ''} ready for analysis.
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                        Click the "Analyze Performance" button to get detailed insights from your calls.
                      </p>
                    </div>
                  )
                ) : (
                  /* User has performance analysis data */
                  <>
                    {/* Analysis Summary Header */}
                    <div className={`rounded-lg p-6 border ${
                      isDarkMode 
                        ? 'bg-gradient-to-r from-green-900/30 to-blue-900/30 border-green-700' 
                        : 'bg-gradient-to-r from-green-50 to-blue-50 border-green-300'
                    }`}>
                      <div className="grid grid-cols-1 md:grid-cols-1 w-full gap-4">
                        <div className="text-center w-full flex flex-col items-center md:text-left">
                          <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                            Performance Rating
                          </h4>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                            {performanceData.current_performance.overall_performance?.rating || 'Developing'}
                          </p>
                        </div>
                        {/* <div className="text-center md:text-right">
                          <div className={`text-4xl mb-2`}>📊</div>
                          <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                            Average Score
                          </h4>
                          <p className={`text-2xl font-bold`} style={{ 
                            color: getPerformanceColor(performanceData.current_performance.overall_performance?.average_score || 0) 
                          }}>
                            {performanceData.current_performance.overall_performance?.average_score?.toFixed(1) || '0.0'}/10
                          </p>
                        </div> */}
                      </div>
                      
                      {performanceData.current_performance.overall_performance?.summary && (
                        <div className="mt-4 pt-4 border-t border-opacity-20">
                          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'} text-center italic`}>
                            "{performanceData.current_performance.overall_performance.summary}"
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Performance Highlights */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={`rounded-lg p-4 border ${
                        isDarkMode 
                          ? 'bg-green-900/30 border-green-700' 
                          : 'bg-green-50 border-green-300'
                      }`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-2xl">💪</div>
                          <h4 className={`${isDarkMode ? 'text-green-400' : 'text-green-700'} font-semibold`}>
                            Top Strength
                          </h4>
                        </div>
                        <p className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                          {performanceData.current_performance.best_performing_metric?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Next Step Commitment'}
                        </p>
                        {performanceData.current_performance.strengths && performanceData.current_performance.strengths.length > 0 && (
                          <ul className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
                            {performanceData.current_performance.strengths.slice(0, 2).map((strength: string, index: number) => (
                              <li key={index} className="flex items-start gap-1">
                                <span className="text-green-500 mt-0.5">•</span>
                                <span>{strength}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className={`rounded-lg p-4 border ${
                        isDarkMode 
                          ? 'bg-yellow-900/30 border-yellow-700' 
                          : 'bg-yellow-50 border-yellow-300'
                      }`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="text-2xl">🎯</div>
                          <h4 className={`${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'} font-semibold`}>
                            Focus Area
                          </h4>
                        </div>
                        <p className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                          {performanceData.current_performance.needs_improvement_metric?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Timeline Discovery'}
                        </p>
                        {performanceData.current_performance.coaching_priorities && performanceData.current_performance.coaching_priorities.length > 0 && (
                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            <p className="font-medium text-xs mb-1">Next Action:</p>
                            <p>{performanceData.current_performance.coaching_priorities[0].action}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Today's Win */}
                    {performanceData.current_performance.wins_today && (
                      <div className={`rounded-lg p-4 border text-center ${
                        isDarkMode 
                          ? 'bg-purple-900/30 border-purple-700' 
                          : 'bg-purple-50 border-purple-300'
                      }`}>
                        <div className="text-3xl mb-2">🏆</div>
                        <h4 className={`${isDarkMode ? 'text-purple-400' : 'text-purple-700'} font-semibold mb-2`}>
                          Yesterday's Victory
                        </h4>
                        <p className={`${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
                          {performanceData.current_performance.wins_today}
                        </p>
                      </div>
                    )}

                    {/* Coaching Priorities */}
                    {performanceData.current_performance.coaching_priorities && performanceData.current_performance.coaching_priorities.length > 0 && (
                      <div className={`rounded-lg p-4 border ${
                        isDarkMode 
                          ? 'bg-indigo-900/30 border-indigo-700' 
                          : 'bg-indigo-50 border-indigo-300'
                      }`}>
                        <h4 className={`${isDarkMode ? 'text-indigo-400' : 'text-indigo-700'} font-semibold mb-4 flex items-center gap-2`}>
                          <span className="text-xl">🎓</span>
                          Coaching Priorities
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {performanceData.current_performance.coaching_priorities.slice(0, 4).map((priority: any, index: number) => (
                            <div key={index} className={`p-3 rounded border ${
                              isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
                            }`}>
                              <div className="flex justify-between items-start mb-2">
                                <h5 className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                                  {priority.area}
                                </h5>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {(typeof priority.current_level === 'number' ? priority.current_level.toFixed(1) : '0.0')}/10 → {(typeof priority.target_level === 'number' ? priority.target_level.toFixed(1) : '0.0')}/10
                                </span>
                              </div>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {priority.action}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Key Recommendations */}
                    {performanceData.current_performance.recommendations && performanceData.current_performance.recommendations.length > 0 && (
                      <div className={`rounded-lg p-4 border ${
                        isDarkMode 
                          ? 'bg-blue-900/30 border-blue-700' 
                          : 'bg-blue-50 border-blue-300'
                      }`}>
                        <h4 className={`${isDarkMode ? 'text-blue-400' : 'text-blue-700'} font-semibold mb-3 flex items-center gap-2`}>
                          <span className="text-xl">💡</span>
                          Key Recommendations
                        </h4>
                        <div className="space-y-2">
                          {performanceData.current_performance.recommendations.map((rec: string, index: number) => (
                            <div key={index} className={`flex items-start gap-3 p-2 rounded ${
                              isDarkMode ? 'bg-[#0a1414]' : 'bg-white'
                            }`}>
                              <span className={`text-blue-500 mt-1 text-sm`}>
                                {index + 1}.
                              </span>
                              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
                                {rec}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Recordings Section */
          <div className="space-y-6">
            {/* Call Summary */}
            {/* <div className={`rounded-xl p-6 shadow-lg ${
              isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
            }`}>
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'} mb-4`}>
                Call Records Overview
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className={`text-center p-4 rounded-lg ${
                  isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'
                }`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    {totalCallsCount}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                    Total Calls
                  </div>
                </div>
                <div className={`text-center p-4 rounded-lg ${
                  isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'
                }`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    {recordings.filter(rec => rec.file_type === 'audio').length}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                    Audio Calls
                  </div>
                </div>
                <div className={`text-center p-4 rounded-lg ${
                  isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'
                }`}>
                  <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                    {recordings.filter(rec => rec.file_type === 'transcript').length}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                    Transcripts
                  </div>
                </div>
              </div>
            </div> */}

            {/* Filters */}
            <CallFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              dateFilter={dateFilter}
              onDateFilterChange={setDateFilter}
              durationFilter={durationFilter}
              onDurationFilterChange={setDurationFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={(startDate, endDate) => 
                setCustomDateRange({ startDate, endDate })
              }
              customDurationRange={customDurationRange}
              onCustomDurationRangeChange={(minDuration, maxDuration) => 
                setCustomDurationRange({ minDuration, maxDuration })
              }
              filteredCount={filteredRecordings.length}
              totalCount={totalCallsCount}
            />

            {/* Score Filter */}
            <div className={`rounded-xl p-4 shadow-lg ${
              isDarkMode ? 'bg-[#0a1414] border border-[#1a2424]' : 'bg-white'
            }`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Award className={`w-5 h-5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Best Performance:
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setScoreFilter('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      scoreFilter === 'all'
                        ? isDarkMode
                          ? 'bg-[#1a9f6b] text-white shadow-lg'
                          : 'bg-[#1a9f6b] text-white shadow-lg'
                        : isDarkMode
                        ? 'bg-[#050a0a] text-gray-400 hover:bg-[#0f1919] hover:text-gray-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                  >
                    All Calls
                  </button>
                  <button
                    onClick={() => setScoreFilter('top5')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      scoreFilter === 'top5'
                        ? isDarkMode
                          ? 'bg-[#1a9f6b] text-white shadow-lg'
                          : 'bg-[#1a9f6b] text-white shadow-lg'
                        : isDarkMode
                        ? 'bg-[#050a0a] text-gray-400 hover:bg-[#0f1919] hover:text-gray-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                  >
                    Top 5 Scores
                  </button>
                  <button
                    onClick={() => setScoreFilter('top10')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      scoreFilter === 'top10'
                        ? isDarkMode
                          ? 'bg-[#1a9f6b] text-white shadow-lg'
                          : 'bg-[#1a9f6b] text-white shadow-lg'
                        : isDarkMode
                        ? 'bg-[#050a0a] text-gray-400 hover:bg-[#0f1919] hover:text-gray-300'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                  >
                    Top 10 Scores
                  </button>
                </div>
                {scoreFilter !== 'all' && (
                  <div className={`ml-auto flex items-center gap-2 px-3 py-1 rounded-lg ${
                    isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
                  }`}>
                    <Star className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Showing {scoreFilter === 'top5' ? 'Top 5' : 'Top 10'} Best Calls
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Recordings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRecordings.map((recording) => (
                <div
                  key={recording.id}
                  className={`rounded-xl p-5 shadow-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-xl border ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-[#0a1414] to-[#050a0a] border-[#1a2424]' 
                      : 'bg-gradient-to-br from-gray-50 to-white border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'} line-clamp-1 mb-1`}>
                        {recording.customer_name}
                      </h4>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {recording.file_type === 'audio' ? '🎵 Audio Call' : '📄 Transcript'}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full border ${getScoreBadgeColor(parseFloat(recording.score))}`}>
                      {recording.score}/10
                    </span>
                  </div>

                  <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{recording.date}</span>
                      {recording.time && (
                        <span className={`ml-auto text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {recording.time}
                        </span>
                      )}
                    </div>
                    {recording.duration && recording.duration !== "N/A" && recording.duration !== "0:00" && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{recording.duration}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span className="truncate">{recording.representative}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        recording.status === 'Completed' ? 'bg-green-500' : 
                        recording.status === 'Processing' ? 'bg-yellow-500' : 'bg-gray-500'
                      }`} />
                      <span className="text-xs">{recording.status}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button 
                      onClick={() => nav(`/analysis/${recording.call_id}`)} 
                      className={`flex-1 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-[#1a9f6b] to-[#148856] hover:from-[#148856] hover:to-[#0f6644] text-white shadow-xl shadow-[#1a9f6b]/30'
                          : 'bg-gradient-to-r from-[#1a9f6b] to-[#148856] hover:from-[#148856] hover:to-[#0f6644] text-white shadow-xl shadow-[#1a9f6b]/30'
                      }`}
                    >
                      <Sparkle className="w-4 h-4" />
                      Analysis
                    </button>
                    <div className="flex gap-1">
                      {recording.file_type === 'audio' && (
                        <button 
                          onClick={() => handleDownloadAudio(recording)}
                          disabled={downloadingCall === recording.call_id}
                          className={`p-2 rounded-lg transition-colors flex items-center justify-center ${
                            downloadingCall === recording.call_id
                              ? `cursor-not-allowed ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-300 text-gray-400'}`
                              : isDarkMode
                              ? 'bg-[#050a0a] hover:bg-[#0f1919] text-gray-300'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                          title={downloadingCall === recording.call_id ? "Downloading..." : "Download audio file"}
                        >
                          {downloadingCall === recording.call_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeleteCall(recording)}
                        disabled={deletingCall === recording.call_id}
                        className={`p-2 rounded-lg transition-colors flex items-center justify-center ${
                          deletingCall === recording.call_id
                            ? `cursor-not-allowed ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-300 text-gray-400'}`
                            : isDarkMode
                            ? 'bg-red-900/20 hover:bg-red-900/40 text-red-400 hover:text-red-300'
                            : 'bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700'
                        }`}
                        title={deletingCall === recording.call_id ? "Deleting..." : "Delete recording"}
                      >
                        {deletingCall === recording.call_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Intersection Observer Target for Lazy Loading */}
            {hasMoreCalls && userCalls.length > 0 && (
              <div 
                ref={observerTarget}
                className="flex items-center justify-center py-8 min-h-[80px]"
              >
                <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {loadingMore ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading more recordings...</span>
                    </div>
                  ) : (
                    <span>Scroll down for more</span>
                  )}
                </div>
              </div>
            )}

            {/* Show count information */}
            {filteredRecordings.length > 0 && (
              <div className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Showing {filteredRecordings.length} of {totalCallsCount} recording{totalCallsCount !== 1 ? 's' : ''}
                {!hasMoreCalls && totalCallsCount > 0 && ' (all loaded)'}
              </div>
            )}

            {filteredRecordings.length === 0 && !loading && (
              <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
                No recordings found matching your criteria.
              </div>
            )}
          </div>
        )}
      </div>
      </>
      )}

      {/* Modal */}
      {modalState.isOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${
            isDarkMode ? 'bg-[#0a1414]/95 border border-[#1a2424] backdrop-blur-md' : 'bg-white/95 border border-gray-200 backdrop-blur-md'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              {modalState.type === 'success' && (
                <CheckCircle className="w-6 h-6 text-green-500" />
              )}
              {modalState.type === 'error' && (
                <AlertCircle className="w-6 h-6 text-red-500" />
              )}
              {modalState.type === 'confirm' && (
                <AlertCircle className="w-6 h-6 text-yellow-500" />
              )}
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                {modalState.title}
              </h3>
            </div>
            
            <p className={`mb-6 ${isDarkMode ? 'text-gray-300' : 'text-[#373737]'}`}>
              {modalState.message}
            </p>
            
            <div className="flex gap-3 justify-end">
              {modalState.type === 'confirm' ? (
                <>
                  <button
                    onClick={closeModal}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      modalState.onConfirm?.();
                      closeModal();
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                </>
              ) : (
                <button
                  onClick={closeModal}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    modalState.type === 'success'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}